import moment from 'moment-timezone';
import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import { DepositBankAccount } from '../deposit-bank-account';
import { Organization } from '../organization';
import { PaymentAttempt } from '../payment-attempt';
import { TransactionAttachment } from '../transaction-attachment';
import { UnifiedBankAccount } from '../unified-bank-account';
import { BankReconciliationTransaction } from '../bank-reconciliation-transaction';
import { PaymentItem } from '../payment-item';
import { TransactionRule } from './transaction-rules/transaction-rule';
import { JournalEntry } from './journal-entries/journal-entry';
import { Transfer } from './transfers/transfer';
import { Category } from '../accounting/category';
import { StripePayout } from './stripe-payout';
import { Vendor } from 'app/models/entities/vendors/vendor';
import { Payable } from '../payable';
import { LedgerEntry } from '../ledger-entry';
import { getConfidenceLevel } from '../../ai-statistics.model';
import Moment = moment.Moment;

export class Transaction
{
    id: number;
    organizationId: number;
    parentId: number;
    vendorId?: number;
    categoryId: number;
    transactionRuleId: number;
    ownerId: number;
    ownerType: string;
    bankAccountId: number;
    amount: number;
    originalAmount: number;
    description: string = '';
    memo: string = '';
    transactionType: string = null;
    legfiDeposit: boolean = false;
    transactionDate: Moment;
    transactionDateString: string = '';
    unitId: number;
    excluded: boolean = false;
    approved: boolean = false;
    journalEntry: boolean = false;
    wasMatched: boolean;
    matchedDate?: string;
    matchedText?: string;
    importedAt: Moment;
    updatedAt: Moment;
    deletedAt: Moment;
    billPaymentId: number;
    linkedPayment: { id: number; model: string; amount: number; sendDate: string; checkNumber: string };
    checkNumber: string;
    isExternalRevenue: boolean;
    allianceTransactionId: number;
    stripeTreasuryTransactionId: number;
    isPending: boolean;
    isPayable: boolean;
    convertToTransaction: boolean;

    aiSuggestion?: {
        suggestedCategoryId: number;
        suggestedCategory: string;
        categoryConfidence: number;  // 0-100
        categoryConfidenceLevel: string;  // 'Low', 'Medium', 'High' based on categoryConfidence
        suggestedVendorId?: number;
        suggestedVendor?: string;
        vendorConfidence?: number;
        vendorConfidenceLevel?: string;  // 'Low', 'Medium', 'High' based on vendorConfidence
        isPending: boolean;
        categoryReasons?: Array<{
            type: string;
            text: string;
            weight: number;
            formattedText: string;
        }>;
        vendorReasons?: Array<{
            type: string;
            text: string;
            weight: number;
            formattedText: string;
        }>;
    };

    attachments: TransactionAttachment[] = [];
    organization: Organization;
    bankAccount: DepositBankAccount | UnifiedBankAccount;
    account: DepositBankAccount | UnifiedBankAccount;
    category: Category;
    vendor: Vendor;
    paymentAttempts: PaymentAttempt[] = [];
    refundedPaymentAttempts: PaymentAttempt[];
    transfer: Transfer;
    children: Transaction[] = [];
    reference: PaymentAttempt | PaymentItem | Payable;
    journalEntries: JournalEntry[];
    transactionRule: TransactionRule = null;
    stripePayout: StripePayout;
    owner: UnifiedBankAccount;
    ledgerEntries: LedgerEntry[] = [];

    // Template control public vars
    public selected: boolean;

    public hidden: boolean = false;
    public checked: boolean = false;
    public showDepositDetail: boolean = false;
    public loadingDetail: boolean = false;
    public isExpandableDeposit: boolean = false;
    public missingFunds: boolean = false;
    public missingAmount: number = 0;
    public isOfflinePayment: boolean = false;

    public editCategory: { id: number, type: string } = null;

    public bankReconciliationTransaction: BankReconciliationTransaction;
    public selectedChild: Transaction = null;

    constructor(transaction: any) {
        this.id = transaction.id || null;
        this.organizationId = transaction.organizationId || null;
        this.parentId = transaction.parentId || null;
        this.bankAccountId = transaction.ownerId || null;
        this.categoryId = transaction.categoryId || null;

        if (transaction.reference) {
            if (transaction.referenceType.indexOf('PaymentAttempt') !== -1) {
                this.reference = new PaymentAttempt(transaction.reference);
            }
            if (transaction.referenceType.indexOf('PaymentItem') !== -1) {
                this.reference = new PaymentItem(transaction.reference);
            }
            if (transaction.referenceType.indexOf('Payable') !== -1) {
                this.reference = new Payable(transaction.reference);
            }
        }

        this.children = transaction.children ? transaction.children.map(c => new Transaction(c)) : [];
        this.owner = transaction.owner ? new UnifiedBankAccount(transaction.owner) : null;

        this.isExternalRevenue = transaction.isExternalRevenue ?? false;
        this.stripeTreasuryTransactionId = transaction.stripeTreasuryTransactionId ?? null;
        this.isPending = transaction.isPending ?? false;
        this.isPayable = transaction.isPayable ?? false;
        this.vendorId = transaction.vendorId ?? null;
        this.transactionRuleId = transaction.transactionRuleId ?? null;

        this.ownerId = transaction.ownerId ?? null;
        this.ownerType = transaction.ownerType ?? null;

        this.excluded = transaction.excluded ?? false;
        this.approved = transaction.approved ?? false;
        this.billPaymentId = transaction.billPaymentId ?? null;
        this.linkedPayment = transaction.linkedPayment ?? null;
        this.checkNumber = transaction.checkNumber ?? null;
        this.journalEntry = transaction.journalEntry ?? false;
        this.wasMatched = transaction.wasMatched ?? false;
        this.matchedDate = transaction.matchedDate;
        if (this.wasMatched) {
            this.matchedText = 'Automatically matched from Bill Pay transaction on ' + this.matchedDate + '.';
        }

        this.amount = transaction.amount;
        this.originalAmount = transaction.originalAmount;
        this.description = transaction.description;
        this.memo = transaction.memo;

        this.legfiDeposit = transaction.transactionType === 'stripe_deposit';
        this.transactionType = transaction.transactionType;

        const timezone = LegFiJwtService.getTimezone();
        if (transaction.transactionDate) {
            this.transactionDate = moment.tz(transaction.transactionDate, 'YYYY-MM-DD HH:mm:ss', timezone);
            this.transactionDateString = this.transactionDate.format('MM/DD/YYYY');
        }
        if (transaction.importedAt) {
            this.importedAt = moment.tz(transaction.importedAt, 'YYYY-MM-DD HH:mm:ss', timezone);
        }
        if (transaction.deletedAt) {
            this.deletedAt = moment.tz(transaction.deletedAt, 'YYYY-MM-DD HH:mm:ss', timezone);
        }

        this.transfer = (transaction.transfer) ? new Transfer(transaction.transfer) : null;

        if (transaction.unitId) {
            this.unitId = transaction.unitId;
        }
        if (transaction.organization) {
            this.organization = new Organization(transaction.organization);
        }
        if (transaction.owner && transaction.ownerType === 'App\\Models\\Entities\\UnifiedBankAccount') {
            this.bankAccount = new UnifiedBankAccount(transaction.owner);
            this.account = new UnifiedBankAccount(transaction.owner);
        }
        if (transaction.category) {
            this.category = new Category(transaction.category);
        }
        if (transaction.attachments) {
            this.attachments = transaction.attachments.map((attachment) => {
                return new TransactionAttachment(attachment);
            });
        }
        if (transaction.journalEntries) {
            this.journalEntries = transaction.journalEntries.map((je) => {
                return new JournalEntry(je);
            });
        }
        if (transaction.paymentAttempts) {
            this.paymentAttempts = transaction.paymentAttempts.map(
                    (paymentAttempt) => {
                        return new PaymentAttempt(paymentAttempt);
                    },
            );
        }

        if (transaction.refundedPaymentAttempts) {
            this.refundedPaymentAttempts = transaction.refundedPaymentAttempts.map(
                    (paymentAttempt) => {
                        return new PaymentAttempt(paymentAttempt);
                    },
            );
        }

        this.transactionRule = transaction.transactionRule
                ? new TransactionRule(transaction.transactionRule)
                : null;

        if (this.children.length > 0) {
            this.isExpandableDeposit = true;
            if (this.transactionType !== 'lockbox_deposit') {
                this.isOfflinePayment = true;
            }
        }

        if (this.legfiDeposit) {
            let childTotal = 0;
            this.children.forEach((child) => {
                childTotal += child.amount;
            });
            if (childTotal > this.originalAmount) {
                this.missingFunds = true;
                this.missingAmount = this.originalAmount - childTotal;
            }
        }

        if (transaction.bankReconciliationTransaction) {
            this.bankReconciliationTransaction = new BankReconciliationTransaction(transaction.bankReconciliationTransaction);
        }

        if (transaction.stripePayout) {
            this.stripePayout = new StripePayout(transaction.stripePayout);
        }

        if (transaction.ledgerEntries) {
            this.ledgerEntries = transaction.ledgerEntries.map((entry: any) => new LedgerEntry(entry));
        }

        if (transaction.aiSuggestion) {
            this.aiSuggestion = {
                suggestedCategoryId: transaction.aiSuggestion.suggestedCategoryId,
                suggestedCategory: transaction.aiSuggestion.suggestedCategory,
                categoryConfidence: transaction.aiSuggestion.categoryConfidence,
                categoryConfidenceLevel: getConfidenceLevel(transaction.aiSuggestion.categoryConfidence),
                suggestedVendorId: transaction.aiSuggestion.suggestedVendorId,
                suggestedVendor: transaction.aiSuggestion.suggestedVendor,
                vendorConfidence: transaction.aiSuggestion.vendorConfidence,
                vendorConfidenceLevel: getConfidenceLevel(transaction.aiSuggestion.vendorConfidence),
                isPending: transaction.aiSuggestion.isPending,
                categoryReasons: transaction.aiSuggestion.categoryReasons || [],
                vendorReasons: transaction.aiSuggestion.vendorReasons || []
            };
        }
    }

    isPayableReference(reference: any): reference is Payable {
        return reference && (reference as Payable).invoiceDate !== undefined;
    }

    updateDepositCategory() {
        // For payables w/ multiple categories, always show "X Categories"
        // This check has to run first, regardless of whether a category is already set
        if (this.isPayable && this.isPayableReference(this.reference)) {
            const expenseCategories = this.reference.expenseCategories;

            if (expenseCategories && expenseCategories.length > 1) {
                this.category = new Category({
                    name: expenseCategories.length + ' Categories',
                });
                return;
            } else if (expenseCategories && expenseCategories.length === 1 && !this.category) {
                const singleCategory = expenseCategories[0];
                this.category = new Category({
                    name: singleCategory.expenseCategory?.label || 'Uncategorized',
                });
                return;
            }
        }

        if (this.children && this.children.length > 0) {
            let cats = [];
            let hasUncategorizedChild = false;

            for (const child of this.children) {
                if (child.children && child.children.length > 0) {
                    for (const item of child.children) {
                        if (item.category?.name) {
                            cats.push(item.category.name);
                        } else {
                            hasUncategorizedChild = true;
                        }
                    }
                } else if (child.category?.name) {
                    cats.push(child.category.name);
                } else {
                    hasUncategorizedChild = true;
                }
            }

            if (hasUncategorizedChild && this.transactionType === 'lockbox_deposit') {
                this.category = null;
                this.categoryId = null;
                this.aiSuggestion = null;
                return;
            }

            cats = cats.filter((v, i) => cats.indexOf(v) === i);
            if (cats.length === 1) {
                this.category = new Category({
                    name: cats[0],
                });
            } else if (cats.length > 1) {
                this.category = new Category({
                    name: cats.length + ' Categories',
                });
            }
        }
    }
}
